/** Composite a slippy-map extract onto a canvas and hand back a PNG data URI,
 * so the day sheet PDF carries its map without the server ever fetching a
 * tile. Ported from the Jinja day sheet, which ported it from the standalone
 * route planner. `tile.openstreetmap.org` (no subdomain) sends
 * `Access-Control-Allow-Origin: *`, which is what keeps the canvas exportable. */

type Point = [number, number];

function project(lat: number, lon: number, z: number): Point {
  const scale = 256 * Math.pow(2, z);
  const x = ((lon + 180) / 360) * scale;
  const y = ((1 - Math.log(Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180)) / Math.PI) / 2) * scale;
  return [x, y];
}

/** The zoom that fits the line in the frame with a margin, 16 down to 9. */
export function fitZoom(line: Point[], width: number, height: number, pad = 46): number {
  const lats = line.map((p) => p[0]);
  const lons = line.map((p) => p[1]);
  const north = Math.max(...lats);
  const south = Math.min(...lats);
  const east = Math.max(...lons);
  const west = Math.min(...lons);
  let z = 16;
  for (; z > 8; z--) {
    const [x1, y1] = project(north, west, z);
    const [x2, y2] = project(south, east, z);
    if (x2 - x1 <= width - 2 * pad && y2 - y1 <= height - 2 * pad) {
      break;
    }
  }
  return z;
}

export async function tileMap(
  line: Point[],
  markers: { lat: number; lon: number; label: string }[],
  width: number,
  height: number,
  color: string,
  approximate: boolean,
): Promise<string> {
  const lats = line.map((p) => p[0]);
  const lons = line.map((p) => p[1]);
  const z = fitZoom(line, width, height);
  const [cx, cy] = project((Math.max(...lats) + Math.min(...lats)) / 2, (Math.max(...lons) + Math.min(...lons)) / 2, z);
  const ox = cx - width / 2;
  const oy = cy - height / 2;

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (ctx === null) {
    throw new Error("No 2D canvas");
  }
  ctx.fillStyle = "#E8EDEB";
  ctx.fillRect(0, 0, width, height);

  const span = Math.pow(2, z);
  const jobs: Promise<void>[] = [];
  for (let tx = Math.floor(ox / 256); tx <= Math.floor((ox + width) / 256); tx++) {
    for (let ty = Math.floor(oy / 256); ty <= Math.floor((oy + height) / 256); ty++) {
      if (ty < 0 || ty >= span) {
        continue;
      }
      const wx = ((tx % span) + span) % span;
      jobs.push(
        new Promise<void>((done) => {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => {
            try {
              ctx.drawImage(img, tx * 256 - ox, ty * 256 - oy);
            } catch {
              // A tainted or broken tile leaves its square grey.
            }
            done();
          };
          img.onerror = () => done();
          img.src = `https://tile.openstreetmap.org/${z}/${wx}/${ty}.png`;
        }),
      );
    }
  }
  await Promise.all(jobs);

  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  const dash = approximate ? [11, 9] : [];
  const trace = () => {
    ctx.setLineDash(dash);
    ctx.beginPath();
    line.forEach((p, i) => {
      const [x, y] = project(p[0], p[1], z);
      if (i === 0) {
        ctx.moveTo(x - ox, y - oy);
      } else {
        ctx.lineTo(x - ox, y - oy);
      }
    });
    ctx.stroke();
  };
  ctx.strokeStyle = "rgba(255,255,255,.85)";
  ctx.lineWidth = 7;
  trace();
  ctx.strokeStyle = color;
  ctx.lineWidth = 3.6;
  trace();
  ctx.setLineDash([]);

  for (const m of markers) {
    const [x, y] = project(m.lat, m.lon, z);
    const px = x - ox;
    const py = y - oy;
    ctx.beginPath();
    ctx.arc(px, py, 11.5, 0, Math.PI * 2);
    ctx.fillStyle = m.label === "S" ? "#101E1C" : color;
    ctx.fill();
    ctx.lineWidth = 2.5;
    ctx.strokeStyle = "#fff";
    ctx.stroke();
    ctx.fillStyle = "#fff";
    ctx.font = "bold 12px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(m.label, px, py);
  }

  ctx.fillStyle = "rgba(255,255,255,.82)";
  ctx.fillRect(0, height - 15, 210, 15);
  ctx.fillStyle = "#444";
  ctx.font = "10px system-ui, sans-serif";
  ctx.textAlign = "left";
  ctx.fillText("Map data © OpenStreetMap contributors", 5, height - 4);
  return canvas.toDataURL("image/png");
}
